import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSingleFlightMutation } from './useSingleFlightMutation';
import type { CompleteRefundTrackingRequest, CreateReceiptRequest } from '@valiosascrm/shared';
import { cashFlowKeys } from '@/hooks/useCashFlow';
import { useFinancialAvailability } from '@/hooks/useFinancialAvailability';
import * as receipts from '@/services/receipts';
import { useReceiptAvailability } from '@/stores/receiptAvailability';

// Story 65.19 — recebimento em partes. Quem não tem `financial:manage` nem pergunta se está
// ligado; desligado ou em erro, a tela se comporta como hoje.

export const receiptKeys = {
  all: ['receipts'] as const,
  obligations: (ids: string[]) => [...receiptKeys.all, 'obligations', [...ids].sort().join(',')] as const,
  refunds: (status: string) => [...receiptKeys.all, 'refunds', status] as const,
};

/**
 * `true` só quando a clínica tem o recebimento em partes ligado e a pessoa pode usá-lo. Não
 * depende de provedor de consulta: é lido dentro de telas que existem há muito tempo.
 */
export function useReceiptsEnabled(): boolean {
  return useFinancialAvailability(useReceiptAvailability, 'financial:manage').write;
}

/**
 * Story 65.22 — `true` também quando o recebimento NOVO está desligado mas a clínica já tem
 * recebimento em partes gravado: o que existe continua à vista e pode ser corrigido.
 */
export function useReceiptsReadable(): boolean {
  return useFinancialAvailability(useReceiptAvailability, 'financial:manage').readable;
}

export function useReceiptObligations(transactionIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: receiptKeys.obligations(transactionIds),
    queryFn: () => receipts.getReceiptObligations(transactionIds),
    enabled: enabled && transactionIds.length > 0,
    staleTime: 0,
    retry: false,
  });
}

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  return useSingleFlightMutation({
    mutationFn: (request: CreateReceiptRequest) => receipts.createReceipt(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.all });
      void queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useRefundTrackings(enabled: boolean) {
  return useQuery({
    queryKey: receiptKeys.refunds('pending'),
    queryFn: () => receipts.getRefundTrackings('pending'),
    enabled,
    retry: false,
  });
}

export function useCompleteRefundTracking() {
  const queryClient = useQueryClient();
  return useSingleFlightMutation({
    mutationFn: ({ id, ...request }: CompleteRefundTrackingRequest & { id: string }) => receipts.completeRefundTracking(id, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.all });
      void queryClient.invalidateQueries({ queryKey: receiptKeys.all });
    },
  });
}
